import { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import { recordHumhubSessionLogin } from "@/lib/authSession";
import type {
  HumhubSessionLoginError,
  HumhubSessionLoginResult,
} from "@/lib/authSessionErrors";
import {
  buildAuthSessionSnapshot,
  buildOidcTokenInfo,
} from "@/lib/authSessionTiming";
import { authApi } from "@/lib/humhub-api/auth";

const parseExpiredAtToMs = (expiredAt: number) =>
  Number.isFinite(expiredAt) ? expiredAt * 1000 : undefined;

export const useHumhubSessionLogin = () => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<HumhubSessionLoginError | null>(null);

  const loginToHumhubSession = useCallback(
    async (
      customAccessToken?: string,
      customAccessTokenExpiresAt?: number,
    ): Promise<HumhubSessionLoginResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const snapshot = buildAuthSessionSnapshot(auth);
        const oidcAccessToken = customAccessToken || snapshot.accessToken;

        if (!oidcAccessToken) {
          const loginError: HumhubSessionLoginError = {
            reason: "missing_access_token",
            message: "No OIDC access token available",
          };
          throw loginError;
        }

        const result = await authApi.oidcLogin(oidcAccessToken);

        if (!result?.auth_token) {
          const loginError: HumhubSessionLoginError = {
            reason: "invalid_response",
            message: result?.message || "No auth token returned",
            details: result,
          };
          throw loginError;
        }

        const oidcToken = buildOidcTokenInfo(
          oidcAccessToken,
          typeof customAccessTokenExpiresAt === "number"
            ? customAccessTokenExpiresAt
            : snapshot.accessTokenExpiresAt,
        );

        if (oidcToken) {
          recordHumhubSessionLogin({
            oidcToken,
            humhubExpiresAt: parseExpiredAtToMs(
              Number.parseInt(result.expired_at, 10),
            ),
          });
        }

        return {
          auth_token: result.auth_token,
          expired_at: Number.parseInt(result.expired_at, 10),
        };
      } catch (err) {
        const errObj =
          typeof err === "object" && err !== null
            ? (err as Record<string, unknown>)
            : {};

        const loginError: HumhubSessionLoginError = {
          reason:
            typeof errObj.reason === "string"
              ? (errObj.reason as HumhubSessionLoginError["reason"])
              : "request_failed",
          message:
            typeof errObj.message === "string"
              ? errObj.message
              : "Unknown error occurred",
          details: "details" in errObj ? errObj.details : err,
        };

        console.error("❌ HumHub login failed:", loginError);
        setError(loginError);
        throw loginError;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  return {
    loginToHumhubSession,
    isLoading,
    error,
  };
};
